// Centralized API configuration
//export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://protoncarwashapi.csnsindia.com:7089/admin';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://protonapi.csnsindia.com/api/admin';
export const IMG_PATH_URL = process.env.IMG_PATH_URL || 'https://protonapi.csnsindia.com';
// Log the API base URL for debugging
console.log('Using API Base URL:', API_BASE_URL);
console.log('Using IMG_PATH_URL :', IMG_PATH_URL);

// Type definitions
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export type UserType = 'customer' | 'admin' | 'staff';

export interface User {
  id?: number;
  user_id: number;
  user_type: UserType;
  name: string;
  email: string;
  mobile: string;
  password_hash: string;
  fcm_token?: string;
  status: 'active' | 'inactive' | 'suspended';
  otp_code?: string;
  otp_expiry?: string;
  email_verified: boolean;
  mobile_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  deleted_at?: string;
  customer_type?: 'Individual' | 'Business';
  profile_photo?: string;
  notification_enabled?: boolean;
  country_id?: number;
}

import { Banner } from 'types';
import { ServiceCategory, ServiceImage } from '../types/service';

// Staff API - Updated to match actual backend routes
export const staffAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sp_status?: string;
    email_verified?: string;
  }) => {
    console.log('🛠️ api.ts staffAPI.getAll called');
    const requestData = {
      page: params?.page || 1,
      limit: params?.limit || 10,
      search: params?.search || '',
      sp_status: params?.sp_status || '',
      email_verified: params?.email_verified || ''
    };

    const response = await fetch(`${API_BASE_URL}/staff/list`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    return handleResponse(response);
  },
  
  create: async (data: any) => {
    console.log('🛠️ api.ts staffAPI.create called');
    // Map frontend fields to backend fields based on Postman collection
    const requestData = {
      sp_name: data.name,
      sp_email: data.email,
      sp_mobile: data.mobile || data.phone,
      password: data.password,
      fcm_token: data.fcm_token || '',
      sp_status: data.status || 'inactive'
    };

    console.log('Creating staff with data:', requestData);

    // Create FormData for multipart form (for profile photo uploads)
    const formData = new FormData();
    Object.keys(requestData).forEach(key => {
      if (requestData[key as keyof typeof requestData] !== null && requestData[key as keyof typeof requestData] !== undefined) {
        formData.append(key, requestData[key as keyof typeof requestData] as any);
      }
    });

    // Add profile photo if provided
    if (data.profile_photo) {
      formData.append('profile_photo', data.profile_photo);
    }

    const response = await fetch(`${API_BASE_URL}/staff/create`, {
      method: 'POST',
      body: formData // Don't set Content-Type header for FormData
    });

    const result = await handleResponse(response);
    console.log('Staff creation response:', result);
    return result;
  },
  
  update: async (id: number, data: any) => {
    console.log('🛠️ api.ts staffAPI.update called with ID:', id);
    console.log('📝 Update data:', data);

    // Create FormData for multipart form
    const formData = new FormData();

    // Map frontend fields to backend fields
    if (data.name) formData.append('sp_name', data.name);
    if (data.email) formData.append('sp_email', data.email);
    if (data.mobile || data.phone) formData.append('sp_mobile', data.mobile || data.phone);
    if (data.fcm_token) formData.append('fcm_token', data.fcm_token);
    if (data.status) formData.append('sp_status', data.status);
    if (data.email_verified !== undefined) formData.append('email_verified', data.email_verified.toString());
    if (data.mobile_verified !== undefined) formData.append('mobile_verified', data.mobile_verified.toString());
    if (data.notification_enabled !== undefined) formData.append('notification_enabled', data.notification_enabled.toString());

    // Add sp_id (required by backend)
    formData.append('sp_id', id.toString());

    // Add profile photo if provided
    if (data.profile_photo) {
      formData.append('profile_photo', data.profile_photo);
    }

    const response = await fetch(`${API_BASE_URL}/staff/update`, {
      method: 'POST',
      body: formData // Don't set Content-Type header for FormData
    });
    return handleResponse(response);
  },
  
  delete: async (id: number) => {
    console.log('🛠️ api.ts staffAPI.delete called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/staff/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_id: id }),
    });
    return handleResponse(response);
  },

  getById: async (id: number) => {
    console.log('🛠️ api.ts staffAPI.getById called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/staff/get-by-id`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_id: id }),
    });
    return handleResponse(response);
  },

  updateStatus: async (id: number, status: string) => {
    console.log('🛠️ api.ts staffAPI.updateStatus called with ID:', id, 'Status:', status);
    const response = await fetch(`${API_BASE_URL}/staff/update-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_id: id, sp_status: status }),
    });
    return handleResponse(response);
  },

  resendOtp: async (email: string) => {
    console.log('🛠️ api.ts staffAPI.resendOtp called with email:', email);
    const response = await fetch(`${API_BASE_URL}/staff/resend-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_email: email }),
    });
    return handleResponse(response);
  },

  verifyOtp: async (email: string, otp: string) => {
    console.log('🛠️ api.ts staffAPI.verifyOtp called with email:', email);
    const response = await fetch(`${API_BASE_URL}/staff/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_email: email, otp }),
    });
    return handleResponse(response);
  },

  changePassword: async (id: number, currentPassword: string, newPassword: string) => {
    console.log('🛠️ api.ts staffAPI.changePassword called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/staff/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sp_id: id, current_password: currentPassword, new_password: newPassword }),
    });
    return handleResponse(response);
  }
};

//const API_BASE_URL = 'http://protoncarwashapi.csnsindia.com:7085/api/customer-requirements';

// Helper function to handle API responses
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  const result = await response.json();

  // Handle the API response structure - some APIs wrap data in ApiResponse, others return directly
  if (result && typeof result === 'object' && 'data' in result) {
    // This is an ApiResponse format
    return result.data as T;
  }

  // Direct data format
  return result as T;
};

// Car Brands API
interface CarBrand {
  id: number;
  name: string;
  logo_url?: string;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export const carBrandsAPI = {
  getAll: async (status?: boolean): Promise<CarBrand[]> => {
    const url = new URL(`${API_BASE_URL}/car-brands`);
    if (status !== undefined) {
      url.searchParams.append('status', status ? '1' : '0');
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-cache',
    });

    const result = await response.json();

    // Extract actual data array safely
    if (result.status === 'success' && result.data?.carBrands) {
      return result.data.carBrands;
    } else {
      console.error('Unexpected response:', result);
      return [];
    }
  },

  getById: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/car-brands/${id}`);
    if (!response.ok) throw new Error('Failed to fetch car brand');
    return response.json();
  },

  create: async (data: { name: string; logo_url?: string; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/car-brands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    if (!response.ok) throw new Error('Failed to create car brand');
    return response.json();
  },

  update: async (id: number, data: { name: string; logo_url?: string; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/car-brands/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    if (!response.ok) throw new Error('Failed to update car brand');
    return response.json();
  },

  delete: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/car-brands/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete car brand');
    return response.json();
  },
};

// Car Models API
interface CarModel {
  id: number;
  brand_id: number;
  name: string;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export const carModelsAPI = {
  getAll: async (brandId?: number, status?: boolean) => {
    const url = new URL(`${API_BASE_URL}/car-models`);

    if (brandId) {
      url.searchParams.append("brand_id", brandId.toString());
    }

    if (status !== undefined) {
      url.searchParams.append("status", status ? "1" : "0");
    }

    // 🔥 Add timestamp to bypass browser cache
    url.searchParams.append("_ts", Date.now().toString());

    // ✅ Disable caching without triggering OPTIONS (CORS)
    const response = await fetch(url.toString(), {
      method: "GET",
      cache: "no-store", // avoids caching safely
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch car models: ${response.statusText}`);
    }

    return response.json();
  },


  getById: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/car-models/${id}`);
    if (!response.ok) throw new Error('Failed to fetch car model');
    return response.json();
  },

  create: async (data: { brand_id: number; name: string; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/car-models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    if (!response.ok) throw new Error('Failed to create car model');
    return response.json();
  },

  update: async (id: number, data: { brand_id: number; name: string; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/car-models/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    if (!response.ok) throw new Error('Failed to update car model');
    return response.json();
  },

  delete: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/car-models/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete car model');
    return response.json();
  },
};

// Service Categories API
export const serviceCategoriesAPI = {
  getAll: async (params?: { status?: boolean | number }) => {
    const formattedParams = {
      ...params,
      status:
        typeof params?.status === 'boolean'
          ? params.status
            ? 1
            : 0
          : params?.status,
    };

    const response = await fetch(
      `${API_BASE_URL}/service-categories`
    );

    const json = await response.json();

    // ✅ Check and extract properly
    if (json?.data?.categories && Array.isArray(json.data.categories)) {
      return json.data.categories;
    } else {
      console.warn("⚠️ Unexpected category data format:", json);
      return [];
    }
  },


  getById: async (id: number): Promise<ServiceCategory> => {
    console.log('🛠️ api.ts serviceCategoriesAPI.getById called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/service-categories/get-by-id`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ category_id: id })
    });
    return handleResponse<ServiceCategory>(response);
  },

  create: async (data: {
    name: string;
    description?: string;
    image_url?: string;
    status?: boolean;
    display_order?: number;
  }): Promise<ServiceCategory> => {
    console.log('🛠️ api.ts serviceCategoriesAPI.create called');
    const requestData = {
      name: data.name,
      description: data.description || '',
      image_url: data.image_url || '',
      status: data.status !== undefined ? data.status : true,
      display_order: data.display_order || 0
    };

    const response = await fetch(`${API_BASE_URL}/service-categories/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    return handleResponse<ServiceCategory>(response);
  },

  update: async (id: number, data: {
    name?: string;
    description?: string;
    image_url?: string;
    status?: boolean;
    display_order?: number;
  }): Promise<ServiceCategory> => {
    console.log('🛠️ api.ts serviceCategoriesAPI.update called with ID:', id);
    const requestData = {
      category_id: id,
      name: data.name,
      description: data.description,
      image_url: data.image_url,
      status: data.status,
      display_order: data.display_order
    };

    const response = await fetch(`${API_BASE_URL}/service-categories/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    return handleResponse<ServiceCategory>(response);
  },

  delete: async (id: number): Promise<void> => {
    console.log('🛠️ api.ts serviceCategoriesAPI.delete called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/service-categories/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ category_id: id })
    });
    return handleResponse<void>(response);
  },

  toggleStatus: async (id: number): Promise<ServiceCategory> => {
    console.log('🛠️ api.ts serviceCategoriesAPI.toggleStatus called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/service-categories/toggle-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ category_id: id })
    });
    return handleResponse<ServiceCategory>(response);
  }
};

// Services API
export const servicesAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    category_id?: number;
    is_active?: boolean;
  }): Promise<any[]> => {
    console.log('🛠️ api.ts servicesAPI.getAll called');
    const url = new URL(`${API_BASE_URL}/services`);
    if (params?.page) url.searchParams.append('page', params.page.toString());
    if (params?.limit) url.searchParams.append('limit', params.limit.toString());
    if (params?.search) url.searchParams.append('search', params.search);
    if (params?.category_id) url.searchParams.append('category_id', params.category_id.toString());
    if (params?.is_active !== undefined) url.searchParams.append('is_active', params.is_active.toString());

    const response = await fetch(url.toString());

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const services = result.data?.services || result.data || result.services || result || [];

    console.log('Services fetched:', services);

    // Transform the data to match the expected format
    return Array.isArray(services) ? services.map((service: any) => ({
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    })) : [];
  },

  getById: async (id: number): Promise<any> => {
    console.log('🛠️ api.ts servicesAPI.getById called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/services/${id}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const service = result.data?.service || result.data || result.service || result;

    if (!service) {
      throw new Error(`Service with id ${id} not found`);
    }

    console.log('Service fetched by ID:', service);

    // Transform the data to match the expected format
    return {
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  },

  create: async (data: {
    service_name: string;
    description?: string;
    base_price: number;
    duration_minutes: number;
    category_id: number;
    is_active?: boolean;
    requirements?: Array<{requirement_id: number, is_mandatory?: boolean}>;
    equipment?: Array<{equipment_id: number}>;
    addons?: Array<{addon_id: number}>;
  }): Promise<any> => {
    console.log('🛠️ api.ts servicesAPI.create called');
    const response = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        ...data,
        is_active: data.is_active !== undefined ? data.is_active : true
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const service = result.data?.service || result.data || result.service || result;

    if (!service) {
      throw new Error('Service creation failed - no service data returned');
    }

    console.log('Service created:', service);

    // Transform the data to match the expected format
    return {
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  },

  update: async (id: number, data: {
    service_name?: string;
    description?: string;
    base_price?: number;
    duration_minutes?: number;
    category_id?: number;
    is_active?: boolean;
    requirements?: Array<{requirement_id: number, is_mandatory?: boolean}>;
    equipment?: Array<{equipment_id: number}>;
    addons?: Array<{addon_id: number}>;
  }): Promise<any> => {
    console.log('🛠️ api.ts servicesAPI.update called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const service = result.data?.service || result.data || result.service || result;

    if (!service) {
      throw new Error('Service update failed - no service data returned');
    }

    console.log('Service updated:', service);

    // Transform the data to match the expected format
    return {
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at
    };
  },

  delete: async (id: number): Promise<void> => {
    console.log('🛠️ api.ts servicesAPI.delete called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE',
    });
    return handleResponse<void>(response);
  },

  getCompleteService: async (id: number): Promise<any> => {
    console.log('🛠️ api.ts servicesAPI.getCompleteService called with ID:', id);
    const response = await fetch(`${API_BASE_URL}/services/get-complete/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ service_id: id })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const service = result.data?.service || result.data || result.service || result;

    if (!service) {
      throw new Error(`Complete service with id ${id} not found`);
    }

    console.log('Complete service fetched:', service);

    // Transform the data to match the expected format
    return {
      service_id: service.service_id || service.id,
      category_id: service.category_id,
      name: service.service_name || service.name,
      description: service.description,
      duration: service.duration_minutes || service.duration,
      base_price: service.base_price || service.price,
      discount_price: service.discount_price,
      image_url: service.image_url,
      is_active: Boolean(service.is_active || service.status),
      display_order: service.display_order || 0,
      created_at: service.created_at,
      updated_at: service.updated_at,
      requirements: service.requirements || [],
      equipment: service.equipment || [],
      addons: service.addons || []
    }
  },

  getAvailableRequirements: async (): Promise<any[]> => {
    console.log('🛠️ api.ts servicesAPI.getAvailableRequirements called');
    const response = await fetch(`${API_BASE_URL}/customer-requirements`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const requirements = result.data?.requirements || result.data || result.requirements || result || [];

    console.log('Available requirements fetched:', requirements);

    // Transform the data to match the expected format
    return Array.isArray(requirements) ? requirements.map((requirement: any) => ({
      requirement_id: requirement.requirement_id || requirement.id,
      name: requirement.name,
      description: requirement.description || '',
      is_mandatory: Boolean(requirement.is_mandatory || requirement.mandatory),
      status: Boolean(requirement.status || requirement.is_active),
      created_at: requirement.created_at,
      updated_at: requirement.updated_at
    })) : [];
  },

  getAvailableEquipment: async (): Promise<any[]> => {
    console.log('🛠️ api.ts servicesAPI.getAvailableEquipment called');
    const response = await fetch(`${API_BASE_URL}/cleaning-equipment`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const equipment = result.data?.equipment || result.data || result.equipment || result || [];

    console.log('Available equipment fetched:', equipment);

    // Transform the data to match the expected format
    return Array.isArray(equipment) ? equipment.map((eq: any) => ({
      equipment_id: eq.equipment_id || eq.id,
      name: eq.name,
      description: eq.description || '',
      quantity_available: eq.quantity_available || eq.quantity || 0,
      status: Boolean(eq.status || eq.is_active),
      created_at: eq.created_at,
      updated_at: eq.updated_at
    })) : [];
  },

  getAvailableAddons: async (): Promise<any[]> => {
    console.log('🛠️ api.ts servicesAPI.getAvailableAddons called');
    const response = await fetch(`${API_BASE_URL}/addon-services`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const result = await response.json();

    // Handle the API response structure - data might be wrapped in various formats
    const addons = result.data?.addons || result.data || result.addons || result || [];

    console.log('Available addons fetched:', addons);

    // Transform the data to match the expected format
    return Array.isArray(addons) ? addons.map((addon: any) => ({
      addon_id: addon.addon_id || addon.id,
      name: addon.name,
      description: addon.description || '',
      base_price: addon.base_price || addon.price || 0,
      duration_minutes: addon.duration_minutes || addon.duration || 0,
      is_active: Boolean(addon.is_active || addon.status),
      display_order: addon.display_order || 0,
      created_at: addon.created_at,
      updated_at: addon.updated_at
    })) : [];
  },

  uploadImages: async (serviceId: number, images: File[]): Promise<ServiceImage[]> => {
    console.log('🛠️ api.ts servicesAPI.uploadImages called with service ID:', serviceId, 'Images count:', images.length);

    if (images.length === 0) {
      return [];
    }

    const formData = new FormData();
    images.forEach((image, index) => {
      formData.append(`images`, image);
      formData.append(`image_${index}_is_primary`, index === 0 ? '1' : '0');
      formData.append(`image_${index}_display_order`, index.toString());
    });

    formData.append('service_id', serviceId.toString());

    try {
      const response = await fetch(`${API_BASE_URL}/service-images/upload`, {
        method: 'POST',
        body: formData // Don't set Content-Type header for FormData
      });

      const result = await handleResponse<{
        images: any[];
        success: boolean;
        message?: string;
      }>(response);

      // Transform the response to match ServiceImage interface
      return result.images.map((image: any, index: number) => ({
        image_id: image.image_id || image.id,
        service_id: serviceId,
        image_url: image.image_url,
        is_primary: image.is_primary || index === 0,
        display_order: image.display_order || index,
        created_at: image.created_at,
        updated_at: image.updated_at
      }));
    } catch (error) {
      console.error('Upload images error:', error);
      throw new Error('Failed to upload service images');
    }
  },
};

// Time Slots API
export const timeSlotsAPI = {
  getAll: async (status?: boolean) => {
    const url = new URL(`${API_BASE_URL}/time-slots`);
    if (status !== undefined) {
      url.searchParams.append('status', status ? '1' : '0');
    }
    const response = await fetch(url.toString());
    return handleResponse(response);
  },

  getById: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/time-slots/${id}`);
    return handleResponse(response);
  },

  create: async (data: { start_time: string; end_time: string; max_bookings?: number; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/time-slots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: { start_time: string; end_time: string; max_bookings?: number; status?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/time-slots/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    return handleResponse(response);
  },

  delete: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/time-slots/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Payment Transactions API
export interface PaymentTransaction {
  payment_transaction_id: string;
  user_id: number;
  customer_name: string;
  customer_email: string;
  booking_id: string;
  booking_number: string;
  amount: number;
  status: 'pending' | 'success' | 'failed';
  payment_method: string;
  transaction_id?: string;
  payment_id: string;
  created_at: string;
  updated_at: string;
}

export const paymentTransactionsAPI = {
  getAll: async (params: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'success' | 'failed';
    search?: string;
  } = {}): Promise<{ data: PaymentTransaction[]; total: number }> => {
    const { page = 1, limit = 10, status, search } = params;
    const url = new URL(`${API_BASE_URL}/payment-transactions`);

    url.searchParams.append('page', page.toString());
    url.searchParams.append('limit', limit.toString());
    if (status) url.searchParams.append('status', status);
    if (search) url.searchParams.append('search', search);

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Request failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If parsing error response fails, use default message
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('Payment transactions API response:', responseData);

      // Handle case where the API returns an object with data and total
      if (responseData && Array.isArray(responseData.data)) {
        return {
          data: responseData.data,
          total: responseData.total || responseData.data.length
        };
      }

      // Handle case where the API returns an array directly
      if (Array.isArray(responseData)) {
        return {
          data: responseData,
          total: responseData.length
        };
      }

      // Default fallback
      return { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching payment transactions:', error);
      throw new Error('Failed to get payment transactions');
    }
  },
  
  getById: async (id: string): Promise<PaymentTransaction> => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment-transactions/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return handleResponse<PaymentTransaction>(response);
    } catch (error) {
      throw new Error('Failed to get payment transaction');
    }
  }
};

// Users API
// Bookings API
export interface Booking {
  id: string;
  booking_number: string;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  customer_mobile: string;
  vehicle_id: number;
  vehicle_number: string;
  status: string;
  payment_status: string;
  total_amount: number;
  payment_method?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  booking_services: Array<{
    id: number;
    service_id: number;
    service_name: string;
    price: number;
    quantity: number;
    total: number;
  }>;
}

export const bookingAPI = {
  getAll: async (params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  } = {}): Promise<{ data: Booking[]; total: number }> => {
    const { page = 1, limit = 10, status, search } = params;
    let url = `${API_BASE_URL}/bookings?page=${page}&limit=${limit}`;
    
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    
    console.log('Making API request to:', url);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    console.log('API response status:', response.status);
    const responseData = await response.json();
    console.log('API response data:', responseData);
    
    // Handle case where the API returns an array directly
    if (Array.isArray(responseData)) {
      return {
        data: responseData,
        total: responseData.length
      };
    }
    
    // Handle case where the API returns an object with data and total
    if (responseData && Array.isArray(responseData.data)) {
      return {
        data: responseData.data,
        total: responseData.total || responseData.data.length
      };
    }
    
    // Default fallback
    return { data: [], total: 0 };
  },

  //getById: async (id: string): Promise<Booking> => {
    getById: async (bookingId: number) => {
    const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Failed to fetch booking");
    const json = await res.json();

    // ✅ Return only the booking object
    return json.data?.booking || {};
  
    //const response = await fetch(`${API_BASE_URL}/bookings/${id}`);
    //return handleResponse(response);
  },
};
export const usersAPI = {
  getAll: async (userType: string, page: number = 1, limit: number = 10) => {
    const query = `?user_type=${userType}&page=${page}&limit=${limit}`;
    const response = await fetch(`${API_BASE_URL}/users${query}`, { method: 'GET' });

    // Parse the JSON
    const res = await response.json();

    // Handle both Option 1 and Option 2 response formats
    const users = res?.data?.users || res?.data?.data?.users || res?.users || [];
    const pagination =
      res?.data?.pagination ||
      res?.data?.data?.pagination ||
      res?.pagination || { total: 0 };

    return { users, pagination };
  },


  getById: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`);
    return handleResponse(response);
  },

  create: async (data: {
    user_type?: UserType;
    name: string;
    email: string;
    mobile: string;
    status?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: {
    user_type?: string;
    name: string;
    email: string;
    mobile: string;
    status?: string;
    email_verified?: boolean;
    mobile_verified?: boolean;
  }) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    return handleResponse(response);
  },

  delete: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};
//Banners
export const createBanner = async (formData: FormData): Promise<Banner> => {
  try {
    console.log('FormData contents:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    const response = await fetch(`${API_BASE_URL}/banners`, {
      method: 'POST',
      // ⚠️ Don't set Content-Type manually (fetch sets it automatically for FormData)
      body: formData,
    });

    const text = await response.text();
    let result;

    try {
      result = JSON.parse(text);
    } catch {
      console.error('Invalid JSON response:', text);
      throw new Error(text);
    }

    if (!response.ok) {
      console.error('API Error Response:', result);
      throw new Error(result?.message || 'Failed to create requirement');
    }

    const requirement = result?.data || result;
    console.log('Customer requirement created:', requirement);
    return requirement;
  } catch (error) {
    console.error('Error creating requirement:', error);
    throw error;
  }
};
export const updateBanner = async (id: number, formData: FormData): Promise<Banner> => {
  try {
    // Log what we're sending for debugging
    console.log('Updating banner with FormData:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'PUT',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(errorText || 'Failed to update banner');
    }

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data' property
    const requirement = result.data.Banner;

    if (!requirement) {
      throw new Error('Failed to update banner - no data returned');
    }

    console.log('banner updated:', requirement);
    return requirement;
    // return handleResponse<Banner>(requirement);
  } catch (error) {
    console.error(`Error updating banner with id ${id}:`, error);
    throw error;
  }
};
export const bannersAPI = {
  getAll: async (): Promise<Banner[]> => {
    try {
      const url = new URL(`${API_BASE_URL}/banners`);
      url.searchParams.append('_t', Date.now().toString()); // unique param
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        cache: "no-store", // stronger than 'no-cache'
      });
      if (!response.ok) {
        console.error(`❌ Get banners failed: ${response.status}`);
        return [];
      }
      const result = await response.json();
      console.log("✅ Banners fetched successfully", result);
      let bannerData =
        result.data?.Banner ??
        result.data ??
        (Array.isArray(result) ? result : []);
      if (!Array.isArray(bannerData)) {
        console.warn("⚠️ No banner array found in response");
        return [];
      }
      return bannerData.map((banner: any) => {
        const rawOrder =
          banner.display_order ?? banner.displayOrder ?? banner.order ?? 0;
        const display_order =
          typeof rawOrder === "string" ? (parseInt(rawOrder, 10) || 0) : (Number(rawOrder) || 0);
        let statusBool = false;
        if (typeof banner.status === "boolean") statusBool = banner.status;
        else if (typeof banner.status === "number") statusBool = banner.status === 1;
        else if (typeof banner.status === "string")
          statusBool = ["1", "true", "yes", "active"].includes(
            banner.status.toLowerCase()
          );
        return {
          banner_id: banner.banner_id ?? banner.id,
          title: banner.title ?? "",
          description: banner.description ?? "",
          image_url: banner.image_url ?? "",
          redirect_url: banner.redirect_url ?? banner.redirectUrl,
          display_order,
          status: statusBool ? 'active' : 'inactive',
          start_date: banner.start_date ?? banner.startDate,
          end_date: banner.end_date ?? banner.endDate,
          created_at: banner.created_at ?? new Date().toISOString(),
          updated_at: banner.updated_at ?? new Date().toISOString(),
        };
      });
    } catch (error) {
      console.error("❌ Error fetching banners:", error);
      throw new Error("Failed to fetch banners");
    }
  },
  create: async (data: {
    title: string;
    description?: string;
    image_url: string;
    redirect_url?: string;
    display_order?: number;
    status?: boolean;
    start_date?: string;
    end_date?: string;
  }): Promise<Banner> => {
    const response = await fetch(`${API_BASE_URL}/banners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    return handleResponse<Banner>(response);
  },
  update: async (id: number, data: {
    title: string;
    description?: string;
    image_url: string;
    redirect_url?: string;
    display_order?: number;
    status?: boolean;
  }): Promise<Banner> => {
    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        status: data.status !== undefined ? (data.status ? 1 : 0) : 1,
      }),
    });
    return handleResponse<Banner>(response);
  },
  uploadImage: async (file: File): Promise<string> => {
      const formData = new FormData();
      formData.append("file", file);
  
      const response = await fetch(`${API_BASE_URL}/banners/uploadImage`, {
        method: "POST",
        body: formData
      });
  
      const result = await response.json();
      return result.filepath; // <-- return the saved file path
    },
  delete: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
  // ...rest of CRUD methods
};
export type { Banner };